import { Blocks, Braces, Database, Gauge, Radio, Search, SlidersHorizontal, Waypoints } from '@bloo-team/ui/icons'
import { ANALYST_OR_ENGINEER, INCLUDING_VIEWER } from '@bloo-team/core-shell'
import type { ConsoleProductManifest } from '@bloo-team/core-shell'
import { IngestHero, SourcesCard, StreamHeat, TopExtractors, VolumeRing } from './home/widgets'
import { fabricMaturitySignals } from './journey/signals'
import { resetAllExtractorOverlays } from './lib/extractor-store'
import { resetAllAuthoringDrafts } from './lib/extractor-authoring-store'
import { resetAllStorageOverlays } from './lib/storage-store'
import { resetAllDataModelOverlays } from './lib/data-model-store'
import { resetAllSourceOverlays } from './lib/source-store'
import { resetAllConnectorOverlays } from './lib/connector-store'

/**
 * Datafabric — the product manifest (docs/product-manifest.md).
 *
 * Pipeline-shaped: the nav order mirrors the data path, sources through
 * the data model (IA9). Lifecycle folded into Storage's Retention tab
 * (docs/storage-requirements.md §The decision that changes the rail);
 * `/fabric/lifecycle` still resolves — it redirects. Extracted to
 * `@bloo-team/product-fabric` in E7: the app's `fabric.*` route files are
 * composer shims over this package's pages.
 * Home widgets: `./home/widgets.tsx` — `order` ranks interleave with the
 * other products' instruments (docs/product-manifest.md).
 */
export const fabricManifest: ConsoleProductManifest = {
  id: 'fabric',
  name: 'Datafabric',
  icon: Database,
  nav: [
    { key: 'fabric-overview', title: 'Overview', icon: Gauge, to: '/fabric/overview', roles: INCLUDING_VIEWER },
    { key: 'fabric-sources', title: 'Sources', icon: Waypoints, to: '/fabric/sources', roles: ANALYST_OR_ENGINEER },
    { key: 'fabric-streams', title: 'Streams', icon: Radio, to: '/fabric/streams', roles: ANALYST_OR_ENGINEER },
    { key: 'fabric-extractors', title: 'Extractors', icon: Braces, to: '/fabric/extractors', roles: ANALYST_OR_ENGINEER },
    { key: 'fabric-enrichment', title: 'Enrichment', icon: Blocks, to: '/fabric/enrichment', roles: ANALYST_OR_ENGINEER },
    { key: 'fabric-storage', title: 'Storage', icon: Database, to: '/fabric/storage', roles: ANALYST_OR_ENGINEER },
    { key: 'fabric-search', title: 'Search', icon: Search, to: '/fabric/search', roles: ANALYST_OR_ENGINEER },
    { key: 'fabric-data-model', title: 'Data Model', icon: SlidersHorizontal, to: '/fabric/data-model', roles: ANALYST_OR_ENGINEER },
  ],
  settings: [],
  palette: [],
  home: [
    { key: 'fabric-home-ingest', title: 'Ingest today', order: 10, render: IngestHero },
    { key: 'fabric-home-volume', title: 'Storage volume', order: 20, render: VolumeRing },
    { key: 'fabric-home-sources', title: 'Inactive sources', order: 70, render: SourcesCard },
    { key: 'fabric-home-streams', title: 'Stream flow', order: 80, render: StreamHeat },
    { key: 'fabric-home-top-extractors', title: 'Top extractors', order: 90, render: TopExtractors },
  ],
  // Home · Journey: the Connect and Understand phases (S3,
  // docs/home-journey-requirements.md §5). Each signal reads through the same
  // hooks the pages use, so Home cannot disagree with what it links to.
  maturity: fabricMaturitySignals,
  // Fabric's demo data is stateless module fixtures (DEMO_SOURCES, the
  // extractor rows, the widget constants; the search fixtures live with the
  // platform engine in @bloo-team/core-shell). The one thing that persists
  // between demo sessions is the engineer's own writes — the localStorage
  // overlays (lib/extractor-store.ts, lib/storage-store.ts,
  // lib/data-model-store.ts, lib/source-store.ts, lib/connector-store.ts).
  // Seeding drops them, so demo returns to fixture state; idempotent, as the
  // contract requires.
  demo: {
    seed: () => {
      resetAllExtractorOverlays()
      resetAllAuthoringDrafts()
      resetAllStorageOverlays()
      resetAllDataModelOverlays()
      resetAllSourceOverlays()
      resetAllConnectorOverlays()
    },
  },
  backend: ['fabric'],
  // Data Model's stream header pivots into SynthAI Knowledge (`?stream=`);
  // the link renders only while both products are registered and entitled
  // (rule 11: coupling declared, never imported — docs/data-model-requirements.md §Pivots).
  integratesWith: ['synthai'],
}
